/**
 * Funnels.tsx — Marketing funnel analytics from Close CRM
 *
 * Blended view (all funnels) up top + per-funnel tabs below.
 * Metrics: Response Rate, Speed to Lead, App Rate, Cash Generated, Close Rate
 * Data source: CloseLead (synced from Close CRM) + EOC + Application/Optin
 */

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area,
} from "recharts";
import { branding } from "../config/branding";
import { useDateRange } from "../context/DateRangeContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface BlendedSummary {
  totalLeads: number;
  respondedLeads: number;
  responseRate: number;
  avgSpeedToLeadSeconds: number | null;
  totalCalls: number;
  totalCloses: number;
  totalCash: number;
  closeRate: number;
  statusBreakdown: { status: string; count: number; responded: boolean }[];
  dailyTrend: { date: string; count: number }[];
  landingPageVisitors?: number;
}

interface FunnelRow {
  funnel: string;
  totalLeads: number;
  totalContacts: number;
  respondedContacts: number;
  responseRate: number;
  callsTaken: number;
  callsClosed: number;
  cashCollected: number;
  closeRate: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000
    ? `$${(n / 1_000).toFixed(1)}K`
    : `$${Math.round(n).toLocaleString()}`;

function fmtSeconds(sec: number | null): string {
  if (sec === null || sec === 0) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h >= 24) return `${Math.round(h / 24)}d`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ─── Scorecard ────────────────────────────────────────────────────────────────

function Scorecard({
  label, value, sub, color, index = 0,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  index?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07, duration: 0.4 }}
      className="bg-card border border-border rounded-xl p-4 flex flex-col gap-1"
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className="text-2xl font-bold"
        style={{ color: color || "var(--foreground)" }}
      >
        {value}
      </p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </motion.div>
  );
}

// ─── Status pill bar ──────────────────────────────────────────────────────────

function StatusPillBar({ breakdown }: { breakdown: { status: string; count: number; responded: boolean }[] }) {
  const total = breakdown.reduce((s, r) => s + r.count, 0);
  if (total === 0) return null;
  const top = [...breakdown].sort((a, b) => b.count - a.count).slice(0, 8);
  return (
    <div className="space-y-1.5">
      {top.map((r) => {
        const pct = ((r.count / total) * 100).toFixed(1);
        return (
          <div key={r.status} className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{
              background: r.responded
                ? branding.colors.chart[0]
                : "var(--muted-foreground)",
            }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs text-foreground truncate max-w-[180px]">{r.status || "Unknown"}</span>
                <span className="text-xs font-semibold text-muted-foreground ml-2">{r.count.toLocaleString()} ({pct}%)</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: r.responded ? branding.colors.chart[0] : "var(--muted-foreground)" }}
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Per-funnel table row ──────────────────────────────────────────────────────

function FunnelTableRow({ row, index, onClick, active }: {
  row: FunnelRow;
  index: number;
  onClick: () => void;
  active: boolean;
}) {
  const chartColors = branding.colors.chart;
  const color = chartColors[index % chartColors.length];
  return (
    <motion.tr
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={onClick}
      className={`cursor-pointer transition-all ${
        active
          ? "bg-[var(--brand-primary)]/10 border-l-[3px] border-[var(--brand-primary)]"
          : "border-l-[3px] border-transparent hover:bg-muted/50"
      }`}
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full flex-shrink-0 transition-all"
            style={{ background: active ? "var(--brand-primary)" : color }}
          />
          <span className={`text-sm truncate max-w-[180px] transition-all ${
            active ? "font-bold text-[var(--brand-primary)]" : "font-medium text-foreground"
          }`}>{row.funnel}</span>
          {active && (
            <span className="ml-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--brand-primary)] bg-[var(--brand-primary)]/15 px-1.5 py-0.5 rounded-full">
              selected
            </span>
          )}
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-right tabular-nums text-foreground">{row.totalLeads.toLocaleString()}</td>
      <td className="py-3 px-4 text-sm text-right tabular-nums">
        <span className={row.responseRate >= 50 ? "text-green-500" : row.responseRate >= 25 ? "text-amber-500" : "text-red-500"}>
          {row.responseRate.toFixed(1)}%
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-right tabular-nums text-foreground">{row.callsTaken}</td>
      <td className="py-3 px-4 text-sm text-right tabular-nums">
        <span className={row.closeRate >= 30 ? "text-green-500" : row.closeRate > 0 ? "text-amber-500" : "text-muted-foreground"}>
          {row.closeRate > 0 ? `${row.closeRate.toFixed(1)}%` : "—"}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-right tabular-nums font-semibold" style={{ color }}>
        {row.cashCollected > 0 ? fmt(row.cashCollected) : "—"}
      </td>
    </motion.tr>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export function Funnels() {
  const { formatForApi, dateRange } = useDateRange();
  const [isLoading, setIsLoading] = useState(true);
  const [blended, setBlended] = useState<BlendedSummary | null>(null);
  const [perFunnel, setPerFunnel] = useState<FunnelRow[]>([]);
  const [selectedFunnel, setSelectedFunnel] = useState<string | null>(null);
  const [funnelDetail, setFunnelDetail] = useState<BlendedSummary | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchBlended = useCallback(async () => {
    setIsLoading(true);
    setBlended(null);
    setPerFunnel([]);
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams({ startDate, endDate });
      const [summaryRes, perFunnelRes] = await Promise.all([
        fetch(`/api/funnels/summary?${params}`),
        fetch(`/api/funnels/per-funnel?${params}`),
      ]);
      const summaryJson = await summaryRes.json() as { success: boolean; data: BlendedSummary };
      const perFunnelJson = await perFunnelRes.json() as { success: boolean; funnels: FunnelRow[] };
      if (summaryJson.success) setBlended(summaryJson.data);
      if (perFunnelJson.success) setPerFunnel(perFunnelJson.funnels);
    } catch (err) {
      console.error("Failed to fetch funnel data", err);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate, formatForApi]);

  const fetchFunnelDetail = useCallback(async (funnel: string) => {
    setDetailLoading(true);
    setFunnelDetail(null);
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams({ startDate, endDate, funnel });
      const res = await fetch(`/api/funnels/summary?${params}`);
      const json = await res.json() as { success: boolean; data: BlendedSummary };
      if (json.success) setFunnelDetail(json.data);
    } catch (err) {
      console.error("Failed to fetch funnel detail", err);
    } finally {
      setDetailLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate, formatForApi]);

  useEffect(() => {
    setSelectedFunnel(null);
    fetchBlended();
  }, [fetchBlended]);

  useEffect(() => {
    if (selectedFunnel) fetchFunnelDetail(selectedFunnel);
    else setFunnelDetail(null);
  }, [selectedFunnel, fetchFunnelDetail]);

  const activeData = selectedFunnel && funnelDetail ? funnelDetail : blended;
  const chartColors = branding.colors.chart;

  return (
    <div className="p-6 space-y-6 max-w-screen-2xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Funnels</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Marketing funnel performance from Close CRM — response rate, speed to lead, and cash generated.
          Use the date filter at the top right to change the time range.
        </p>
      </div>

      {/* Blended Scorecards */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          {selectedFunnel ? `Funnel: ${selectedFunnel}` : "All Funnels (Blended)"}
        </p>
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-4 h-20 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Scorecard
              index={0}
              label="Total Leads"
              value={(activeData?.totalLeads ?? 0).toLocaleString()}
              color={`var(--brand-primary)`}
            />
            <Scorecard
              index={1}
              label="Response Rate"
              value={`${(activeData?.responseRate ?? 0).toFixed(1)}%`}
              sub={`${(activeData?.respondedLeads ?? 0).toLocaleString()} responded`}
              color={
                (activeData?.responseRate ?? 0) >= 50
                  ? chartColors[0]
                  : (activeData?.responseRate ?? 0) >= 25
                  ? "#f59e0b"
                  : "#ef4444"
              }
            />
            <Scorecard
              index={2}
              label="Speed to Lead"
              value={fmtSeconds(activeData?.avgSpeedToLeadSeconds ?? null)}
              sub="median first call"
            />
            <Scorecard
              index={3}
              label="Close Rate"
              value={`${(activeData?.closeRate ?? 0).toFixed(1)}%`}
              sub={`${(activeData?.totalCloses ?? 0)} / ${(activeData?.totalCalls ?? 0)} calls`}
              color={chartColors[0]}
            />
            <Scorecard
              index={4}
              label="Cash Generated"
              value={fmt(activeData?.totalCash ?? 0)}
              sub="from EOC calls"
              color={`var(--brand-primary)`}
            />
            <Scorecard
              index={5}
              label="Landing Page Visitors"
              value={(activeData?.landingPageVisitors ?? 0).toLocaleString()}
              sub={selectedFunnel ? "unique visitors (GA)" : "unique visitors (GA)"}
              color={chartColors[1]}
            />
          </div>
        )}
      </div>

      {/* Charts row */}
      {!isLoading && activeData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Lead trend */}
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-sm font-semibold text-foreground mb-4">Lead Volume Trend</p>
            {activeData.dailyTrend.length === 0 ? (
              <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={activeData.dailyTrend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="leadGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={branding.colors.gradient.start} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={branding.colors.gradient.start} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                    tickFormatter={(v: string) => v.slice(5)}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                  <Tooltip
                    contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }}
                    labelStyle={{ color: "var(--foreground)", fontSize: 12 }}
                    itemStyle={{ color: "var(--foreground)", fontSize: 12 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={branding.colors.gradient.start}
                    fill="url(#leadGradient)"
                    strokeWidth={2}
                    dot={false}
                    name="Leads"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Status breakdown */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-foreground">Contact Outcome Breakdown</p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: chartColors[0] }} />
                  Responded
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full inline-block bg-muted-foreground" />
                  No Response
                </span>
              </div>
            </div>
            {activeData.statusBreakdown.length === 0 ? (
              <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">No data</div>
            ) : (
              <StatusPillBar breakdown={activeData.statusBreakdown} />
            )}
          </div>
        </div>
      )}

      {/* Per-Funnel Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="text-sm font-semibold text-foreground">Per-Funnel Breakdown</p>
          {selectedFunnel && (
            <button
              onClick={() => setSelectedFunnel(null)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
            >
              Clear selection
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-8 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : perFunnel.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <p className="text-sm">No funnel data yet.</p>
            <p className="text-xs mt-1">Close leads are syncing every 4 hours — check back shortly.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-2.5 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Funnel</th>
                  <th className="text-right py-2.5 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Leads</th>
                  <th className="text-right py-2.5 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Response Rate</th>
                  <th className="text-right py-2.5 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Calls Taken</th>
                  <th className="text-right py-2.5 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Close Rate</th>
                  <th className="text-right py-2.5 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cash</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {perFunnel.map((row, i) => (
                  <FunnelTableRow
                    key={row.funnel}
                    row={row}
                    index={i}
                    active={selectedFunnel === row.funnel}
                    onClick={() => setSelectedFunnel(prev => prev === row.funnel ? null : row.funnel)}
                  />
                ))}
              </tbody>
              {/* Totals row */}
              <tfoot>
                <tr className="border-t-2 border-border bg-muted/20">
                  <td className="py-3 px-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Total</td>
                  <td className="py-3 px-4 text-sm text-right font-bold tabular-nums text-foreground">
                    {perFunnel.reduce((s, r) => s + r.totalLeads, 0).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-bold tabular-nums">
                    {(() => {
                      const totalC = perFunnel.reduce((s, r) => s + r.totalContacts, 0);
                      const responded = perFunnel.reduce((s, r) => s + r.respondedContacts, 0);
                      return totalC > 0 ? `${((responded / totalC) * 100).toFixed(1)}%` : "—";
                    })()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-bold tabular-nums text-foreground">
                    {perFunnel.reduce((s, r) => s + r.callsTaken, 0)}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-bold tabular-nums">
                    {(() => {
                      const calls = perFunnel.reduce((s, r) => s + r.callsTaken, 0);
                      const closes = perFunnel.reduce((s, r) => s + r.callsClosed, 0);
                      return calls > 0 ? `${((closes / calls) * 100).toFixed(1)}%` : "—";
                    })()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-bold tabular-nums" style={{ color: `var(--brand-primary)` }}>
                    {fmt(perFunnel.reduce((s, r) => s + r.cashCollected, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Funnel detail drill-down */}
      <AnimatePresence>
        {selectedFunnel && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="bg-card border border-border rounded-xl p-5"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-bold text-foreground">{selectedFunnel}</p>
                <p className="text-xs text-muted-foreground">Funnel detail view</p>
              </div>
              <button
                onClick={() => setSelectedFunnel(null)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-lg hover:bg-muted"
              >
                ✕ Close
              </button>
            </div>

            {detailLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-muted rounded-xl p-4 h-16 animate-pulse" />
                ))}
              </div>
            ) : funnelDetail ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <Scorecard label="Leads" value={(funnelDetail.totalLeads).toLocaleString()} index={0} color="var(--brand-primary)" />
                  <Scorecard
                    label="Response Rate"
                    value={`${funnelDetail.responseRate.toFixed(1)}%`}
                    sub={`${funnelDetail.respondedLeads.toLocaleString()} responded`}
                    index={1}
                    color={funnelDetail.responseRate >= 50 ? chartColors[0] : funnelDetail.responseRate >= 25 ? "#f59e0b" : "#ef4444"}
                  />
                  <Scorecard label="Speed to Lead" value={fmtSeconds(funnelDetail.avgSpeedToLeadSeconds)} sub="median first call" index={2} />
                  <Scorecard
                    label="Close Rate"
                    value={`${funnelDetail.closeRate.toFixed(1)}%`}
                    sub={`${funnelDetail.totalCloses} / ${funnelDetail.totalCalls} calls`}
                    index={3}
                    color={chartColors[0]}
                  />
                  <Scorecard label="Cash" value={fmt(funnelDetail.totalCash)} index={4} color="var(--brand-primary)" />
                  <Scorecard
                    label="Landing Page Visitors"
                    value={(funnelDetail.landingPageVisitors ?? 0).toLocaleString()}
                    sub="unique visitors (GA)"
                    index={5}
                    color={chartColors[1]}
                  />
                </div>

                {/* Status breakdown for this funnel */}
                {funnelDetail.statusBreakdown.length > 0 && (
                  <div className="bg-muted/30 rounded-xl p-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Contact Outcomes</p>
                    <StatusPillBar breakdown={funnelDetail.statusBreakdown} />
                  </div>
                )}
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footnote */}
      <p className="text-xs text-muted-foreground/60">
        Response rate = 0.21 contact made activities where outcome ≠ &quot;No Response&quot; ÷ total contact attempts.
        Close data syncs from Close CRM every 15 minutes.
      </p>
    </div>
  );
}
